import type { ApiClient } from './client';
import { pageLimit } from './products';

/**
 * Distribution regions and the settings that point at them, D11.
 *
 * Read off `veri-plus-backend`: `routes/v1/region.routes.ts` (CRUD at
 * `/v1/regions`, any member reads, admins write), `region.validation.ts`
 * (`setDistributionSchema`), and `region.service.ts`, whose `resolveFor` walks
 * batch, then product, then organization, and the most specific setting wins.
 * Every document arrives through its `toJSON`, so `_id` is `id`, and an unset
 * setting is ABSENT rather than null (`distributionSchemaField`).
 */

export interface Region {
  id: string;
  name: string;
  /** ISO 3166-1 alpha-2, upper case, deduplicated by the API. */
  countries: string[];
}

export type DistributionSetting = { mode: 'region'; regionId: string } | { mode: 'unrestricted' };

/** A level's own setting. `undefined` is "nobody said here", so the level above applies. */
export type OwnSetting = DistributionSetting | undefined;

/** `regionController.list` answers `{ regions, nextCursor }`. Every page is loaded, as D7 does for products. */
export async function listAllRegions(api: ApiClient, signal?: AbortSignal): Promise<Region[]> {
  const all: Region[] = [];
  let cursor: string | null = null;
  const seen = new Set<string>();
  do {
    const query: string = cursor === null ? '' : `&cursor=${encodeURIComponent(cursor)}`;
    const page: { regions: Region[]; nextCursor: string | null } = await api.get(
      `/v1/regions?limit=${pageLimit}${query}`,
      signal ? { signal } : {},
    );
    all.push(...page.regions);
    cursor = page.nextCursor ?? null;
    if (cursor !== null) {
      if (seen.has(cursor)) break;
      seen.add(cursor);
    }
  } while (cursor !== null);
  return all;
}

/** `POST /v1/regions`, `createRegionSchema`. Org admins only. The screen draws no form for it yet, DEFERRED 4.2. */
export async function createRegion(
  api: ApiClient,
  fields: { name: string; countries: string[] },
): Promise<Region> {
  const body = await api.post<{ region: Region }>('/v1/regions', fields);
  return body.region;
}

/** `DELETE /v1/regions/:regionId`, 204. Refused with 409 `region_in_use` while anything points at it. */
export async function deleteRegion(api: ApiClient, regionId: string): Promise<void> {
  await api.delete<unknown>(`/v1/regions/${encodeURIComponent(regionId)}`);
}

/** The organization's own setting, from `GET /v1/organization`. This is the brand default. */
export async function getBrandDefault(api: ApiClient, signal?: AbortSignal): Promise<OwnSetting> {
  const body = await api.get<{ organization: { distribution?: DistributionSetting } }>(
    '/v1/organization',
    signal ? { signal } : {},
  );
  return body.organization.distribution;
}

/** The fields of `GET /v1/code-batches/:batchId` the picker reads. */
export interface Batch {
  id: string;
  productId: string;
  provenance?: { batchNumber?: string };
  distribution?: DistributionSetting;
}

export async function getBatch(
  api: ApiClient,
  batchId: string,
  signal?: AbortSignal,
): Promise<Batch> {
  const body = await api.get<{ batch: Batch }>(
    `/v1/code-batches/${encodeURIComponent(batchId)}`,
    signal ? { signal } : {},
  );
  return body.batch;
}

/** The fields of `GET /v1/products/:productId` the picker reads. */
export interface ProductSetting {
  id: string;
  name: string;
  distribution?: DistributionSetting;
}

export async function getProductSetting(
  api: ApiClient,
  productId: string,
  signal?: AbortSignal,
): Promise<ProductSetting> {
  const body = await api.get<{ product: ProductSetting }>(
    `/v1/products/${encodeURIComponent(productId)}`,
    signal ? { signal } : {},
  );
  return body.product;
}

/**
 * `PATCH /v1/code-batches/:batchId/distribution`. `null` clears the batch's own
 * setting so it inherits again, which the API keeps distinct from
 * `unrestricted` on purpose.
 */
export async function setBatchDistribution(
  api: ApiClient,
  batchId: string,
  distribution: DistributionSetting | null,
): Promise<Batch> {
  const body = await api.patch<{ batch: Batch }>(
    `/v1/code-batches/${encodeURIComponent(batchId)}/distribution`,
    {
      distribution,
    },
  );
  return body.batch;
}

/** `PATCH /v1/products/:productId/distribution`, same body. Used by the live test to clean up. */
export async function setProductDistribution(
  api: ApiClient,
  productId: string,
  distribution: DistributionSetting | null,
): Promise<void> {
  await api.patch<unknown>(`/v1/products/${encodeURIComponent(productId)}/distribution`, {
    distribution,
  });
}

/** What a batch that says nothing of its own inherits, and from which level. */
export type Inherited =
  | { status: 'region'; level: 'product' | 'organization'; regionId: string }
  | { status: 'unrestricted'; level: 'product' | 'organization' }
  | { status: 'unset' };

/**
 * `regionService.resolveFor` with the batch level left out: product, then
 * organization. A region setting with no `regionId` falls through, as the
 * service's does.
 */
export function inherited(product: OwnSetting, organization: OwnSetting): Inherited {
  const levels: ['product' | 'organization', OwnSetting][] = [
    ['product', product],
    ['organization', organization],
  ];
  for (const [level, setting] of levels) {
    if (!setting) continue;
    if (setting.mode === 'unrestricted') return { status: 'unrestricted', level };
    if (setting.regionId) return { status: 'region', level, regionId: setting.regionId };
  }
  return { status: 'unset' };
}

/** `GET /v1/reports/distribution`, `reportService.distribution`. */
export interface DistributionReport {
  scans: {
    judged: number;
    outOfRegion: number;
    inconclusive: number;
    inconclusiveByReason: Record<string, number>;
  };
  coverage: {
    batches: { total: number; withoutSetting: number };
    products: { total: number; withoutSetting: number };
  };
}

/** No window: the board names none, and coverage counts are not windowed by the API. */
export function getDistributionReport(
  api: ApiClient,
  signal?: AbortSignal,
): Promise<DistributionReport> {
  return api.get<DistributionReport>('/v1/reports/distribution', signal ? { signal } : {});
}

/** One coverage card. An empty string is a value the board draws and the data cannot fill. */
export interface CoverageCard {
  label: string;
  value: string;
  note: string;
  tone: 'suspect' | 'muted';
}

/**
 * The three cards `PortalRegions` draws. "N not configured" is the API's
 * `withoutSetting`, which counts a level's OWN setting only, DEFERRED 4.6. The
 * note is withheld at zero, as D9 withholds a delta that did not happen. The
 * declined share is inconclusive over every scan the check looked at.
 */
export function coverageCards(report: DistributionReport): CoverageCard[] {
  const { batches, products } = report.coverage;
  const looked = report.scans.judged + report.scans.inconclusive;
  const configured = (
    label: string,
    c: { total: number; withoutSetting: number },
  ): CoverageCard => ({
    label,
    value: `${(c.total - c.withoutSetting).toLocaleString('en-US')} of ${c.total.toLocaleString('en-US')}`,
    note: c.withoutSetting > 0 ? `${c.withoutSetting.toLocaleString('en-US')} not configured` : '',
    tone: 'suspect',
  });
  return [
    configured('Batches with a region', batches),
    configured('Products with a region', products),
    {
      label: 'Declined to judge',
      value: looked > 0 ? `${((report.scans.inconclusive / looked) * 100).toFixed(1)}%` : '',
      note: 'near a border or poor GPS',
      tone: 'muted',
    },
  ];
}

/** "Batch #B-1042", from the pack's own batch number. No number, no label: the board draws none. */
export const batchLabel = (batch: Batch) =>
  batch.provenance?.batchNumber ? `Batch #${batch.provenance.batchNumber}` : '';
